import classNames from 'classnames';
import flatpickr from 'flatpickr';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import Flatpickr from 'react-flatpickr';
import { useTranslation } from 'react-i18next';
import MaskedInput from 'react-text-mask';
import isFunction from 'lodash/isFunction';

import { IconCalendar } from 'src/components/Icons/Calendar';
import { maskedInputConvertMask } from 'src/components/UI/inputs/CleanMaskedInput';
import { getInputError } from 'src/components/UI/inputs/common';
import { useHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { FormPropsType } from 'src/components/UI/types';
import { uiMemoFormPropsInputCompareFn } from 'src/components/UI/UIMemoFormPropsCompareFn';
import { CDate } from 'src/utils/CDate';
import { inputBaseHook } from 'src/utils/hooks/inputs';
import { SafeAnyType } from 'src/utils/safeAny';
import { UiHoverTooltip } from './HoverTooltip';

type Props = {
  formProps: FormPropsType;
  name: string;

  required?: boolean;
  minDate?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  readonly?: boolean;
  onChange?: (value: string | null) => void;
  maxDate?: string | number;
  showHoverError?: boolean;
  material?: boolean;
  showClearBtn?: boolean;
};

const UiDatePickerItself: React.FC<Props> = memo(
  ({
    formProps: {
      register,
      getValues,
      setValue,
      formState: { errors },
      clearErrors,
    },
    required,
    name,
    minDate,
    disabled,
    className,
    placeholder = 'mm/dd/yyyy',
    autoFocus,
    readonly,
    onChange: onChangeDate,
    maxDate,
    showHoverError = false,
    material = false,
    showClearBtn,
  }: Props) => {
    const { t } = useTranslation();

    const mountRef = useRef(false);

    useEffect(() => inputBaseHook(mountRef, name, clearErrors), [clearErrors, name]);

    useEffect(() => {
      register(name, {
        /** required only when mounted */
        validate: required ? (v) => (mountRef.current ? !!v : true) : undefined,
      });
    }, [name, register, required]);

    const value = getValues(name);

    const error = getInputError(errors, name);

    /** check if there are any errors */
    const errorRef = useRef(false);

    useEffect(() => {
      errorRef.current = !!error;
    }, [error]);
    /** end check if there are any errors */

    /** prepare the value */
    const getValue = useCallback((val: Date | string | null): string | undefined => {
      if (val) {
        return val instanceof Date ? CDate.format(val, 'MM/dd/yyyy') : val;
      }
      // must return an empty string so that the value is changed correctly via setValue
      return '';
    }, []);

    /** memoize the options */
    const options = useMemo((): flatpickr.Options.Options => {
      const getMinDate = () => {
        const val = getValue(value);

        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0); // should be the date of beginning of the day

        if (val) {
          const [month, day, year] = val.split('/');
          const valueDate = new Date(+year, +month - 1, +day);

          return valueDate > currentDate ? currentDate : valueDate;
        }

        return 'today';
      };

      return {
        monthSelectorType: 'dropdown',
        dateFormat: 'm/d/Y', // 'm/d/Y h:i K' with time
        minDate: minDate ? getMinDate() : undefined,
        shorthandCurrentMonth: true,
        showMonths: 1,
        maxDate,
        onReady: (_, b, { close, open, calendarContainer }) => {
          // `!mountRef.current` for ignoring opening later. it happens just in the beggining
          if (!mountRef.current && autoFocus && !disabled && !value) {
            // open it just a little bit later
            setTimeout(open, 0);
          }

          if (material || showClearBtn) {
            const whereToInsert = calendarContainer?.querySelector('.flatpickr-rContainer');

            if (whereToInsert) {
              const div = document.createElement('div');
              div.className = 'flatpickr-clear';
              div.innerHTML = `<button type="button" class="flatpickr-clear-button">${t('common.clear')}</button>`;

              whereToInsert.appendChild(div);

              const button = div.querySelector('button');

              if (button) {
                button.addEventListener('click', () => {
                  close();

                  setValue(name, null);
                });
              }
            }
          }
        },
      };
      // don't depend on value. take it once at start
      // eslint-disable-next-line
    }, [minDate, maxDate, getValue, autoFocus, disabled, material, t, setValue, name, showClearBtn]);

    const { hoverMessage, handleChangeInputHover } = useHoverErrors({
      name,
      clearErrors,
      error: getInputError(errors, name),
      showAnyError: showHoverError,
    });

    const onChange = useCallback(
      (_: unknown, strDate: string, instance: SafeAnyType) => {
        handleChangeInputHover();

        const selectedDate = strDate.length ? strDate : null;
        const isChangedDate = selectedDate !== getValues(name);

        if (isChangedDate) {
          if (required && errorRef.current) {
            setValue(name, selectedDate, { shouldValidate: true });
          } else {
            setValue(name, selectedDate); // the problem of rerender
          }

          if (isFunction(onChangeDate)) {
            onChangeDate(selectedDate);
          }
        }

        /** close on change especially when pass a date via input */
        instance.close();
      },
      [getValues, handleChangeInputHover, name, onChangeDate, required, setValue]
    );

    /** m/d/y */
    const arrMask = useMemo(() => maskedInputConvertMask('19/39/9999'), []);

    const ref = useRef<Flatpickr>(null);

    const Component = (
      <Flatpickr
        ref={ref}
        options={options as SafeAnyType} // types?
        className="flatpick-custom"
        onChange={onChange}
        render={({ defaultValue }, refPicker) => (
          <UiHoverTooltip id={name} text={hoverMessage} render={!!hoverMessage && !material} noWrapper>
            <MaskedInput
              defaultValue={defaultValue}
              mask={arrMask}
              disabled={disabled}
              placeholder={placeholder}
              value={getValue(value)}
              data-name={name}
              readOnly={readonly}
              className={classNames(className, {
                required: !!error && required,
                error: !!error,
                disabled,
              })}
              render={(refInput, props) => (
                <input
                  ref={(element) => {
                    if (element) {
                      refPicker(element);
                      refInput(element);
                    }
                  }}
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...props}
                />
              )}
            />
          </UiHoverTooltip>
        )}
      />
    );

    return material ? (
      <div className="date-picker-material r-date-picker">
        <span className="input-with-icon">
          {Component}
          <span role="presentation" onClick={() => ref.current?.flatpickr.open()}>
            <IconCalendar />
          </span>
        </span>
      </div>
    ) : (
      Component
    );
  },
  uiMemoFormPropsInputCompareFn
);

export const UiDatePicker: React.FC<Props> = (props) => {
  // eslint-disable-next-line react/destructuring-assignment
  const value = props.formProps.watch(props.name);

  // eslint-disable-next-line react/jsx-props-no-spreading,react/destructuring-assignment
  return <UiDatePickerItself {...props} key={`${props.name}_${value}`} />;
};
